import { Component } from '@theme/component';
import { STORE_DATA } from './zendesk-store-data.js';

const TICKET_FORM_ID_DEFAULT = '17036078165263';

const STATE_FIELD_ID = '17027077736591';
const PROJECT_FIELD_ID = '17028483746063';
const INTEREST_FIELD_ID = '17028475429903';
const FIRST_NAME_FIELD_ID = '17026816974863';
const LAST_NAME_FIELD_ID = '17026807998095';
const EMAIL_FIELD_ID = '17026899326223';
const PHONE_FIELD_ID = '17026909763471';
const VIP_FIELD_ID = '17028488130191';
const TERMS_FIELD_ID = '17028509975055';
const PREFERRED_DAY_FIELD_ID = '17720495791631';
const PREFERRED_TIME_FIELD_ID = '17720533941007';


/**
 * @typedef {object} ZendeskAppointmentFormRefs
 * @property {HTMLFormElement} [form]
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLElement} [successMessage]
 * @property {HTMLSelectElement} [stateSelect]
 * @property {HTMLElement} [stepStore]
 * @property {HTMLSelectElement} [storeSelect]
 * @property {HTMLElement} [stepProject]
 * @property {HTMLSelectElement} [projectSelect]
 * @property {HTMLElement} [stepInterest]
 * @property {HTMLSelectElement} [interestSelect]
 * @property {HTMLElement} [stepContact]
 */

/**
 * Progressive appointment request form. Submits to the Zendesk Requests API
 * against the "Request an appointment" ticket form.
 *
 * @extends {Component<ZendeskAppointmentFormRefs>}
 */
class ZendeskAppointmentForm extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#populateStateOptions();
    this.refs.form?.addEventListener('submit', this.#handleSubmit);
    this.refs.stateSelect?.addEventListener('change', this.#handleStateChange);
    this.refs.projectSelect?.addEventListener('change', this.#handleProjectChange);
    this.refs.interestSelect?.addEventListener('change', this.#handleInterestChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form?.removeEventListener('submit', this.#handleSubmit);
    this.refs.stateSelect?.removeEventListener('change', this.#handleStateChange);
    this.refs.projectSelect?.removeEventListener('change', this.#handleProjectChange);
    this.refs.interestSelect?.removeEventListener('change', this.#handleInterestChange);
    this.#activeFetch?.abort();
  }

  #populateStateOptions() {
    const select = this.refs.stateSelect;
    if (!(select instanceof HTMLSelectElement)) return;

    for (const [value, data] of Object.entries(STORE_DATA)) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = data.label;
      select.append(option);
    }
  }

  #handleStateChange = () => {
    const select = this.refs.stateSelect;
    const storeSelect = this.refs.storeSelect;
    const stepStore = this.refs.stepStore;
    const stepProject = this.refs.stepProject;

    if (!(select instanceof HTMLSelectElement) || !(storeSelect instanceof HTMLSelectElement)) {
      return;
    }

    const stateValue = select.value;
    const data = STORE_DATA[stateValue];

    storeSelect.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.textContent = storeSelect.dataset.placeholder || 'Select a Store';
    storeSelect.append(placeholder);

    if (data) {
      for (const option of data.options) {
        const optionEl = document.createElement('option');
        optionEl.value = option.value;
        optionEl.textContent = option.label;
        storeSelect.append(optionEl);
      }
    }

    if (stepStore instanceof HTMLElement) {
      stepStore.hidden = !stateValue;
    }
    if (stepProject instanceof HTMLElement) {
      stepProject.hidden = !stateValue;
    }

    // Collapse later steps if the person changes their state selection after progressing
    this.#hideStep(this.refs.stepInterest);
    this.#hideStep(this.refs.stepContact);
  };

  #handleProjectChange = () => {
    const select = this.refs.projectSelect;
    const stepInterest = this.refs.stepInterest;

    if (!(select instanceof HTMLSelectElement) || !(stepInterest instanceof HTMLElement)) return;

    stepInterest.hidden = !select.value;
    this.#hideStep(this.refs.stepContact);
  };

  #handleInterestChange = () => {
    const select = this.refs.interestSelect;
    const stepContact = this.refs.stepContact;

    if (!(select instanceof HTMLSelectElement) || !(stepContact instanceof HTMLElement)) return;

    stepContact.hidden = !select.value;
  };

  /**
   * @param {HTMLElement | undefined} step
   */
  #hideStep(step) {
    if (step instanceof HTMLElement) {
      step.hidden = true;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = this.refs.form;
    if (!(form instanceof HTMLFormElement)) return;

    this.#hideMessages();

    if (!form.checkValidity()) {
      form.reportValidity();
      this.#showError('Please complete all required fields.');
      return;
    }

    const formData = new FormData(form);
    const state = String(formData.get('state') ?? '').trim();
    const store = String(formData.get('store') ?? '').trim();
    const project = String(formData.get('project') ?? '').trim();
    const interest = String(formData.get('interest') ?? '').trim();
    const firstName = String(formData.get('first_name') ?? '').trim();
    const lastName = String(formData.get('last_name') ?? '').trim();
    const preferredDay = String(formData.get('preferred_day') ?? '').trim();
    const preferredTime = String(formData.get('preferred_time') ?? '').trim();
    const email = String(formData.get('email') ?? '').trim();
    const phone = String(formData.get('phone') ?? '').trim();
    let formattedPhone = '';
    if (phone) {
      formattedPhone = this.#formatPhone(phone) ?? '';
      if (!formattedPhone) {
        this.#showError(
          this.dataset.phoneInvalidMessage ||
            'Please enter a valid mobile number, e.g. 0412 345 678.'
        );
        return;
      }
    }
    const isVip = formData.get('beaumont_vip') === 'on';
    const acceptedTerms = formData.get('terms') === 'on';

    if (!acceptedTerms) {
      this.#showError('Please accept the Terms & Conditions to continue.');
      return;
    }

    const storeData = STORE_DATA[state];
    if (!storeData) {
      this.#showError('Please select a valid state.');
      return;
    }

    const subdomain = this.dataset.subdomain?.trim();
    const endpointOverride = this.dataset.endpoint?.trim();
    const endpoint =
      endpointOverride ||
      (subdomain ? `https://${subdomain}.zendesk.com/api/v2/requests.json` : '');

    if (!endpoint) {
      this.#showError(
        this.dataset.configurationErrorMessage ||
          'Contact form is not configured. Add a Zendesk subdomain in theme settings.'
      );
      return;
    }

    const ticketFormId = this.dataset.ticketFormId?.trim() || TICKET_FORM_ID_DEFAULT;
    const subjectPrefix = this.dataset.subjectPrefix || 'Appointment Request';
    const requesterName = [firstName, lastName].filter(Boolean).join(' ').trim() || email;

    const commentBody = [
      `State: ${storeData.label}`,
      `Store: ${this.#getSelectedLabel(this.refs.storeSelect)}`,
      `Project: ${this.#getSelectedLabel(this.refs.projectSelect)}`,
      `Interest: ${this.#getSelectedLabel(this.refs.interestSelect)}`,
      `Preferred Day: ${preferredDay}`,
      `Preferred Time: ${preferredTime}`,
      `Name: ${requesterName}`,
      `Email: ${email}`,
      `Phone: ${formattedPhone}`,
      `Beaumont VIP opt-in: ${isVip ? 'Yes' : 'No'}`,
    ].join('\n');

    const customFields = [
      { id: Number(STATE_FIELD_ID), value: state },
      { id: Number(PROJECT_FIELD_ID), value: project },
      { id: Number(INTEREST_FIELD_ID), value: interest },
      { id: Number(PREFERRED_DAY_FIELD_ID), value: preferredDay },
      { id: Number(PREFERRED_TIME_FIELD_ID), value: preferredTime },
      { id: Number(FIRST_NAME_FIELD_ID), value: firstName },
      { id: Number(LAST_NAME_FIELD_ID), value: lastName },
      { id: Number(EMAIL_FIELD_ID), value: email },
      { id: Number(VIP_FIELD_ID), value: isVip },
      { id: Number(TERMS_FIELD_ID), value: acceptedTerms },
    ];

    // Only include the store field for the selected state; Zendesk's tagger
    // fields reject an empty string as still "blank", so omit the rest entirely.
    customFields.push({ id: Number(storeData.fieldId), value: store });

    if (formattedPhone) {
      customFields.push({ id: Number(PHONE_FIELD_ID), value: formattedPhone });
    }

    const payload = {
      request: {
        requester: {
          name: requesterName,
          email,
        },
        subject: `${subjectPrefix}: ${storeData.label}`,
        comment: {
          body: commentBody,
        },
        ticket_form_id: Number(ticketFormId),
        tags: ['website', 'appointment-request'],
        custom_fields: customFields,
      },
    };

    this.#setSubmitting(true);

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!response.ok) {
        console.error('Zendesk appointment form payload', payload);
        const errorMessage = await this.#getErrorMessage(response);
        throw new Error(errorMessage);
      }

      form.reset();
      this.#resetSteps();
      this.#showSuccess(
        this.dataset.successMessage ||
          "Thanks for your appointment request. Our team will be in touch to confirm a time."
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      this.#showError(
        error instanceof Error && error.message
          ? error.message
          : this.dataset.errorMessage ||
              'Something went wrong while submitting your request. Please try again.'
      );
    } finally {
      this.#activeFetch = null;
      this.#setSubmitting(false);
    }
  };

  #resetSteps() {
    this.#hideStep(this.refs.stepStore);
    this.#hideStep(this.refs.stepProject);
    this.#hideStep(this.refs.stepInterest);
    this.#hideStep(this.refs.stepContact);

    const storeSelect = this.refs.storeSelect;
    if (storeSelect instanceof HTMLSelectElement) {
      storeSelect.innerHTML = '';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = storeSelect.dataset.placeholder || 'Select a Store';
      storeSelect.append(placeholder);
    }
  }

  /**
   * Normalises an Australian mobile number to "04XX XXX XXX".
   * Returns null if the digits don't form a valid AU mobile number.
   * @param {string} rawPhone
   * @returns {string | null}
   */
  #formatPhone(rawPhone) {
    const digits = rawPhone.replace(/\D/g, '');
    if (!/^04\d{8}$/.test(digits)) return null;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  }

  /**
   * @param {HTMLSelectElement | undefined} select
   */
  #getSelectedLabel(select) {
    if (!(select instanceof HTMLSelectElement)) return '';
    return select.selectedOptions[0]?.textContent?.trim() || '';
  }

  /**
   * @param {boolean} isSubmitting
   */
  #setSubmitting(isSubmitting) {
    const submitButton = this.refs.form?.querySelector('button[type="submit"]');
    if (!(submitButton instanceof HTMLButtonElement)) return;

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute('aria-busy', String(isSubmitting));
  }

  /**
   * @param {HTMLElement} container
   * @param {string} message
   */
  #setMessageText(container, message) {
    const textNode = container.querySelector('span');
    if (textNode) {
      textNode.textContent = message;
    } else {
      container.append(message);
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const errorMessage = this.refs.errorMessage;
    if (!(errorMessage instanceof HTMLElement)) return;

    this.#setMessageText(errorMessage, message);
    errorMessage.hidden = false;
    errorMessage.focus();
  }

  /**
   * @param {string} message
   */
  #showSuccess(message) {
    const successMessage = this.refs.successMessage;
    if (!(successMessage instanceof HTMLElement)) return;

    this.#setMessageText(successMessage, message);
    successMessage.hidden = false;
    successMessage.focus();
  }

  #hideMessages() {
    for (const element of [this.refs.errorMessage, this.refs.successMessage]) {
      if (!(element instanceof HTMLElement)) continue;
      element.hidden = true;
      this.#setMessageText(element, '');
    }
  }

  /**
   * @param {Response} response
   */
  async #getErrorMessage(response) {
    const fallback =
      this.dataset.errorMessage ||
      'Something went wrong while submitting your request. Please try again.';

    try {
      const data = await response.json();
      const details = data?.details?.base || data?.base;

      if (Array.isArray(details)) {
        const messages = details
          .map((detail) => detail?.description || detail?.message)
          .filter(Boolean);

        if (messages.length) {
          return messages.join(' ');
        }
      }

      if (typeof data?.description === 'string' && data.description) {
        return data.description;
      }

      if (typeof data?.error === 'string' && data.error) {
        return data.error;
      }
    } catch (parseError) {
      console.error('Failed to parse Zendesk error response', parseError);
    }

    return fallback;
  }
}

if (!customElements.get('zendesk-appointment-form')) {
  customElements.define('zendesk-appointment-form', ZendeskAppointmentForm);
}